import type { WorkOrderStatus } from '../../shared/work-order-status';
import type { JobType, MediaType, PhotoCategory, ReadDirection } from '../../shared/domain';

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/**
 * A request that never reached the Worker — offline, or the server is down.
 *
 * This used to also carry the "your Cloudflare Access session expired" case,
 * where Access answered an API call with a cross-origin redirect that `fetch`
 * could not follow and reported as a bare `TypeError: Failed to fetch`. With
 * authentication handled inside the Worker, an expired session is an ordinary
 * 401 with a JSON body, so that guesswork — and the one-shot auto-reload that
 * went with it — is gone.
 */
export class NetworkError extends Error {
  readonly offline: boolean;

  constructor(message: string, offline: boolean) {
    super(message);
    this.name = 'NetworkError';
    this.offline = offline;
  }
}

/** True when the server says this request needs a (fresh) sign-in. */
export function isUnauthenticated(err: unknown): boolean {
  return err instanceof ApiError && err.status === 401;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      ...init,
      // The session cookie is same-origin, but being explicit keeps this
      // correct if the app is ever served from a different host than the API.
      credentials: 'same-origin',
      headers: {
        ...(init?.body !== undefined && !(init.body instanceof Uint8Array)
          ? { 'Content-Type': 'application/json' }
          : {}),
        ...init?.headers,
      },
    });
  } catch {
    const offline = typeof navigator !== 'undefined' && !navigator.onLine;
    throw new NetworkError(
      offline ? 'You appear to be offline.' : 'Could not reach the server. Try again in a moment.',
      offline,
    );
  }

  const text = await res.text();
  let body: unknown = null;
  if (text !== '') {
    try {
      body = JSON.parse(text);
    } catch {
      // Access serves an HTML login page on some misconfigurations; that is
      // not an API failure the user can act on by reading the HTML.
      if (!res.ok) throw new ApiError(res.status, text, `Request failed (${res.status})`);
      throw new ApiError(res.status, text, 'Server returned a non-JSON response.');
    }
  }

  if (!res.ok) {
    const message =
      typeof body === 'object' && body !== null && 'message' in body
        ? String((body as { message: unknown }).message)
        : `Request failed (${res.status})`;
    throw new ApiError(res.status, body, message);
  }

  return body as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  post: <T>(path: string, data?: unknown) =>
    request<T>(path, {
      method: 'POST',
      ...(data === undefined ? {} : { body: JSON.stringify(data) }),
    }),
  patch: <T>(path: string, data: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(data) }),
  put: <T>(path: string, data: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(data) }),
};

// --- Shapes returned by the Worker ---

export interface WorkOrderRow {
  wo: {
    id: string;
    woNumber: string;
    status: WorkOrderStatus;
    jobType: JobType;
    requestedInstallDate: string | null;
    requestedInstallDateText: string | null;
    deadlineOn: string | null;
    campaignName: string | null;
    needsManualUnit: boolean;
    dispatchedAt: number | null;
    submittedAt: number | null;
  };
  unitCode: string | null;
  unitCity: string | null;
  clientName: string | null;
  installerName: string | null;
}

export interface Summary {
  byStatus: Record<WorkOrderStatus, number>;
  needsAttention: {
    draftsPendingReview: number;
    unacknowledgedDispatches: number;
    inProgress: number;
    awaitingApproval: number;
  };
}

export interface UnitRow {
  id: string;
  unitCode: string;
  geopathId: string | null;
  description: string | null;
  city: string | null;
  state: string | null;
  dma: string | null;
  mediaType: MediaType;
  readDirection: ReadDirection | null;
  sizeText: string | null;
  latitude: number | null;
  longitude: number | null;
  isActive: boolean;
  defaultInstallerId: string | null;
  defaultInstallerName: string | null;
}

export interface InstallerRow {
  id: string;
  companyName: string;
  primaryContactName: string | null;
  email: string;
  phone: string | null;
  capCrane: boolean;
  capBucket: boolean;
  capHighClimb: boolean;
  capDigital: boolean;
  insuranceExpiresOn: string | null;
  w9OnFile: boolean;
  isActive: boolean;
}

export interface WorkOrderDetail {
  workOrder: WorkOrderRow['wo'] & {
    copyDescription: string | null;
    specialInstructions: string | null;
    installerNotes: string | null;
    dimensionsText: string | null;
    readDirection: ReadDirection | null;
    materialCarrier: string | null;
    materialTrackingNumber: string | null;
    pdfR2Key: string | null;
    createdBy: string | null;
    dispatchedBy: string | null;
    approvedBy: string | null;
    sourceEmailId: string | null;
  };
  unit: UnitRow | null;
  client: { id: string; name: string } | null;
  installer: InstallerRow | null;
  items: {
    id: string;
    label: string;
    isRequired: boolean;
    isCompleted: boolean;
    installerNote: string | null;
  }[];
  photos: {
    id: string;
    category: PhotoCategory;
    approvedForClient: boolean;
    exifLatitude: number | null;
    exifLongitude: number | null;
  }[];
  invoices: { id: string; amountCents: number; invoiceNumber: string | null; approvalStatus: string }[];
  emails: {
    id: string;
    direction: string;
    subject: string | null;
    status: string;
    createdAt: number;
    toAddresses: string[];
  }[];
  attachments: { id: string; filename: string; kind: string }[];
  allowedTransitions: WorkOrderStatus[];
}

export function formatMoney(cents: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}

export function formatDateTime(ms: number | null): string {
  if (ms === null) return '—';
  return new Date(ms).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
